import { Box, InputBase } from '@mui/material';
import { styled, type CSSObject, type Theme } from '@mui/material/styles';
import { infoSectionTitleStyles } from '@/components/Approval/@components/InfoSection/styles';

/**
 * 결재 양식 본문용 표·입력 스타일 — 레거시 품의 폼(.table / .input-invisible / .btn-table-x 등) 값 그대로.
 * 루트 클래스 `approval-form-table`, 수정 가능한 표는 `is-editable` 로 구분
 */

// 앱 다크 모드(styles/appDarkMode.ts)에선 `--dk-*` 로 — 고정 연회색이면 머리줄이 밝은 글자와 겹쳐 안 보인다
export const APPROVAL_TABLE_BORDER = 'var(--dk-line-strong, #E0E2E5)';
export const APPROVAL_TABLE_HEAD_BG = 'var(--dk-bg-muted, #F9FAFB)';

export const ApprovalFormTableScroll = styled(Box)({
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
});

/** 표 스타일 — 컴포넌트와 저장 HTML(`table.approval-form-table`) 공용 */
export const approvalFormTableStyles = (theme: Theme): CSSObject => ({
  width: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
  fontSize: 14,
  lineHeight: '20px',
  color: 'var(--dk-text, #1A1A1A)',
  [theme.breakpoints.down('md')]: {
    minWidth: 800,
  },
  '& th, & td': {
    border: `1px solid ${APPROVAL_TABLE_BORDER}`,
    verticalAlign: 'middle',
    wordBreak: 'break-all',
  },
  '& th': {
    padding: '8px 4px',
    lineHeight: 1.4,
    backgroundColor: APPROVAL_TABLE_HEAD_BG,
    fontWeight: 500,
    textAlign: 'center',
  },
  '& td': {
    height: 40,
    padding: 6,
  },
  '& tbody td': { backgroundColor: 'var(--dk-bg, #FFF)' },
  '&.is-editable td': { padding: 0 },
  '& .align-left': { textAlign: 'left' },
  '& .align-right': { textAlign: 'right' },
  '& .align-center': { textAlign: 'center' },
  '& tfoot tr:first-of-type': {
    borderTop: `3px solid ${APPROVAL_TABLE_BORDER}`,
  },
  '& tfoot td': { backgroundColor: APPROVAL_TABLE_HEAD_BG },
  '& strong': { fontWeight: 600 },
});

export const ApprovalFormTableRoot = styled('table')(({ theme }) =>
  approvalFormTableStyles(theme),
);

/** 셀을 채우는 투명 입력(레거시 .input-invisible) */
export const ApprovalTableCellInput = styled(InputBase)({
  width: '100%',
  height: '100%',
  minHeight: 40,
  boxSizing: 'border-box',
  border: '1px solid var(--dk-bg, #FFF)',
  fontSize: 14,
  lineHeight: 1,
  '& input': {
    padding: '6px 4px',
    height: 'auto',
  },
  '&:hover': {
    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
    borderColor: 'var(--dk-line, #F9FAFB)',
  },
  '&.Mui-focused': {
    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
    borderColor: '#000',
  },
  '&.align-right input': { textAlign: 'right' },
  '&.align-center input': { textAlign: 'center' },
});

/**
 * 편집 표 안 자동 계산 칸(레거시 회색 disabled 입력) — td 자체라 행 높이가 바뀌어도 꽉 참.
 * 표의 `tbody td`·`is-editable td` 규칙보다 우선하도록 클래스 3중
 */
export const ApprovalTableCalculatedCell = styled('td')({
  '&&&': {
    padding: '6px 4px',
    backgroundColor: 'var(--dk-bg-muted, #F3F5F6)',
    textAlign: 'right',
  },
  '&.is-empty': { color: 'var(--dk-text-dim, rgba(183,187,194,1))' },
});

/** 표 위 오른쪽 단위 표시 — 「(단위 : 원)」 */
export const ApprovalTableUnit = styled('p')({
  margin: 0,
  fontSize: 12,
  lineHeight: '18px',
  textAlign: 'right',
  color: 'var(--dk-text-strong, #4D5157)',
});

/** 행 삭제 X(레거시 .btn-table-x) — 13px 빨간 대각선 */
export const ApprovalTableRowDeleteButton = styled('button')({
  position: 'relative',
  display: 'inline-block',
  verticalAlign: 'middle',
  width: 20,
  height: 20,
  padding: 0,
  border: 0,
  borderRadius: 2,
  backgroundColor: 'var(--dk-bg, #FFF)',
  cursor: 'pointer',
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: 13,
    height: 1,
    backgroundColor: '#F54F61',
    transformOrigin: 'center',
  },
  '&::before': { transform: 'translate(-50%, -50%) rotate(45deg)' },
  '&::after': { transform: 'translate(-50%, -50%) rotate(-45deg)' },
  '&:hover': { backgroundColor: 'rgba(245,79,97,0.16)' },
  '&:active': { backgroundColor: 'rgba(245,79,97,0.24)' },
  '&:disabled': {
    cursor: 'not-allowed',
    backgroundColor: 'var(--dk-bg, #FFF)',
  },
  '&:disabled::before, &:disabled::after': {
    backgroundColor: 'var(--dk-bg-strong, #D1D8E2)',
  },
});

/** 행 삭제 X 에 마우스를 올린 행(레거시 tr.del) */
export const ApprovalTableRow = styled('tr', {
  shouldForwardProp: (prop) => prop !== 'deleteHover',
})<{ deleteHover?: boolean }>(({ deleteHover }) =>
  deleteHover
    ? {
        '& > td:not(:last-child)': {
          backgroundColor: 'rgba(245,80,96,0.26)',
          opacity: 0.4,
        },
        '& > td:not(:last-child) .MuiInputBase-root': {
          borderColor: 'transparent',
          backgroundColor: 'transparent',
        },
        '& > td:last-child': {
          backgroundColor: 'rgba(245,80,96,0.12)',
        },
      }
    : {},
);

/** 표 아래 「항목 추가」 바(레거시 .btn-table-add) */
export const ApprovalTableAddRowButton = styled('button')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '10px 0',
  border: '1px solid var(--dk-line, #E7E9EC)',
  borderTop: 0,
  outline: 0,
  backgroundColor: 'var(--dk-bg, #FFF)',
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: 1,
  color: '#3498DB',
  cursor: 'pointer',
  '& svg': { width: 12, height: 12, marginRight: 5, color: '#3498DB' },
  '&:hover, &:focus-visible': {
    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
  },
  '&:active': { backgroundColor: 'var(--dk-bg-strong, #E7E9EC)' },
});

/** 조회 값 박스(레거시 view .input-box-layout). `filled` = 회색 배경 */
/** 값 박스 스타일 — 컴포넌트와 저장 HTML(`.approval-value-box`, `.is-filled`) 공용 */
export const approvalValueBoxStyles = (filled: boolean): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  minHeight: 44,
  boxSizing: 'border-box',
  padding: '0 8px 0 12px',
  border: '1px solid var(--dk-line, #E7E9EC)',
  borderRadius: 2,
  backgroundColor: filled
    ? 'var(--dk-bg-subtle, #F9FAFB)'
    : 'var(--dk-bg, #FFF)',
  fontSize: 14,
  lineHeight: '20px',
  color: 'var(--dk-text, #1A1A1A)',
  wordBreak: 'break-all',
});

export const ApprovalValueBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'filled',
})<{ filled?: boolean }>(({ filled }) => approvalValueBoxStyles(!!filled));

/** 작성 텍스트 입력(레거시 .input.outline-black). 모서리는 포탈 공통 입력과 같게 */
export const ApprovalOutlineInput = styled(InputBase)({
  width: '100%',
  height: 39,
  boxSizing: 'border-box',
  border: '1px solid var(--dk-line-strong, #D3D3D3)',
  borderRadius: 8,
  backgroundColor: 'var(--dk-bg, #FFF)',
  fontSize: 14,
  transition: 'border-color 0.1s ease',
  '& input': {
    padding: '8px 10px',
    height: 'auto',
    '&::placeholder': {
      color: 'var(--dk-text-dim, rgba(183,187,194,1))',
      opacity: 1,
    },
  },
  '&:hover, &.Mui-focused': { borderColor: '#131313' },
});

/** 입력 지우기 버튼(레거시 .input-btn-area .btn-delete, svg close-circle 20 · #d7d7d7) */
export const ApprovalInputClearButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
  marginRight: 10,
  padding: 0,
  border: 0,
  background: 'none',
  cursor: 'pointer',
  lineHeight: 1,
  '& svg': { width: 20, height: 20, '--bg-color': '#D7D7D7' },
});

/** 작성 select(레거시 .select-type-arrow.outline-black) — 공통 Select 의 sx 로 넘김. 모서리는 포탈 공통 입력과 같게 */
export const approvalOutlineSelectSx = {
  height: 39,
  borderRadius: '8px',
  fontSize: 14,
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--dk-line-strong, #D3D3D3)',
    borderRadius: '8px',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#131313 !important',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#131313 !important',
  },
} as const;

/** 섹션 여닫기(레거시 view .btn-toggle-amount) */
/** 섹션 여닫기 스타일 — 버튼과 저장 HTML(`summary.approval-section-toggle`) 공용 */
export const approvalSectionToggleStyles: CSSObject = {
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  width: '100%',
  padding: '10px 0 8px',
  border: 'none',
  borderBottom: '1px solid #000',
  background: 'none',
  fontFamily: 'inherit',
  cursor: 'pointer',
  textAlign: 'left',
  '&:hover': { backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)' },
  '&:active': { backgroundColor: 'var(--dk-bg-muted, #F3F5F6)' },
  '& > strong': infoSectionTitleStyles,
  '& > svg': {
    width: 12,
    height: 12,
    margin: '0 3px',
    color: 'var(--dk-text, #1A1A1A)',
  },
};

export const ApprovalSectionToggleButton = styled('button')(
  approvalSectionToggleStyles,
);

/** 표 칸을 채우는 테두리 없는 select — 공통 Select 의 sx 로 넘김 */
export const approvalTableCellSelectSx = {
  width: '100%',
  // 전역 테마 InputBase 높이 44 를 이기려면 같은 특이도로 지정
  '&.MuiInputBase-root': { height: 40 },
  borderRadius: 0,
  fontSize: 14,
  backgroundColor: 'transparent',
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid transparent',
    borderRadius: 0,
  },
  '&:hover': { backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)' },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--dk-line, #F9FAFB)',
  },
  '&.Mui-focused': { backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #000',
  },
  '&.Mui-disabled': { backgroundColor: 'transparent' },
} as const;

/** 통화 선택 버튼(레거시 .custom-select-btn) — MuiSelect sx */
export const approvalCurrencySelectSx = {
  width: '100%',
  height: '100%',
  minHeight: 40,
  fontSize: 14,
  '& .MuiOutlinedInput-notchedOutline': {
    border: '1px solid transparent',
    borderRadius: 0,
  },
  '&:hover': { backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)' },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'var(--dk-line, #F9FAFB)',
  },
  '&.Mui-focused': { backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)' },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '1px solid #000',
  },
  '&& .MuiSelect-select.MuiInputBase-input': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // 전역 테마의 select gap 32·MUI `&&&` 우측 패딩 32 를 이겨야 화살표가 값 옆에 붙음
    gap: '4px !important',
    padding: '6.5px 10px',
    paddingRight: '10px !important',
    minHeight: 0,
  },
  '& .arrow': {
    width: 10,
    height: 10,
    flexShrink: 0,
    color: 'var(--dk-text-strong, #666)',
  },
} as const;

export const approvalCurrencyMenuPaperSx = {
  marginTop: '5px',
  width: 224,
  border: '1px solid #000',
  borderRadius: '8px',
  boxShadow: 'none',
  '& .MuiList-root': { padding: '10px 8px', maxHeight: 320 },
} as const;

export const CurrencyOption = styled('span')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  width: '100%',
  '& .content': { display: 'flex', alignItems: 'center', gap: 8 },
  '& .name': { display: 'flex', flexDirection: 'column', lineHeight: '18px' },
  '& .name strong': { fontWeight: 600, color: 'var(--dk-text, #000)' },
  '& .name small': { fontSize: 12, color: 'var(--dk-text-secondary, #888D96)' },
  '& .check': {
    width: 12,
    height: 12,
    color: 'var(--dk-text-disabled, #D1D8E2)',
  },
  '.MuiMenuItem-root:hover & .check, .Mui-selected & .check': {
    color: 'var(--dk-text, #000)',
  },
});
